import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, ExternalLink, Mail, Pin, Trash2 } from 'lucide-react';
import type {
  DraftOutreachResponse,
  OutreachTemplateKind,
  SupplierPipelineStatus,
} from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../design';
import { ApiCallError } from '../lib/api';
import {
  initialOutreachComposerState,
  outreachComposerReducer,
  useDraftOutreach,
  useSupplierMessages,
} from '../lib/outreach';
import { useDeletePricingSheet, usePricingSheets } from '../lib/pricing-sheets';
import { useDeleteSupplier, useSupplier, useUnpinAsin, useUpdateSupplier } from '../lib/suppliers';
import { PricingSheetUploadModal } from '../components/PricingSheetUploadModal';
import { OutreachComposer } from './app/OutreachComposer';

/**
 * Which supplier statuses unlock which outreach template. Mirrors the
 * server-side gating in `apps/api/src/routes/outreach.ts` — keep in sync.
 */
const TEMPLATE_REQUIRED_STATUS: Record<OutreachTemplateKind, SupplierPipelineStatus> = {
  cold_intro: 'lead',
  follow_up: 'contacted',
  pricing_request: 'replied',
};

const TEMPLATE_LABELS: Record<OutreachTemplateKind, string> = {
  cold_intro: 'Cold intro — first touch',
  follow_up: 'Follow-up — bump after silence',
  pricing_request: 'Pricing request — terms ask',
};

/**
 * After "Mark as sent" we advance the supplier to the next pipeline stage
 * IF the next transition is ours to make. `follow_up` is intentionally
 * omitted: the next stage (`replied`) is the supplier's action, not the
 * operator's, so sending another follow-up doesn't move the pipeline.
 */
const MARK_SENT_TRANSITIONS: Partial<Record<OutreachTemplateKind, SupplierPipelineStatus>> = {
  cold_intro: 'contacted',
  pricing_request: 'negotiating',
};

const STAGES_WITHOUT_TEMPLATES: SupplierPipelineStatus[] = ['negotiating', 'active', 'passed'];

const STATUS_OPTIONS: Array<{ value: SupplierPipelineStatus; label: string }> = [
  { value: 'lead', label: 'Lead — discovered, not contacted' },
  { value: 'contacted', label: 'Contacted — outreach sent' },
  { value: 'replied', label: 'Replied — they responded' },
  { value: 'negotiating', label: 'Negotiating — terms in flight' },
  { value: 'active', label: 'Active — buying from them' },
  { value: 'passed', label: 'Passed — not pursuing' },
];

const STATUS_LABELS: Record<SupplierPipelineStatus, string> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label.split(' — ')[0]!])
) as Record<SupplierPipelineStatus, string>;

function statusBadgeVariant(status: SupplierPipelineStatus): 'neutral' | 'accent' | 'positive' {
  if (status === 'active') return 'positive';
  if (status === 'negotiating' || status === 'replied') return 'accent';
  return 'neutral';
}

export function SupplierDetailPage() {
  const { id } = useParams({ from: '/suppliers/$id' });
  const navigate = useNavigate();
  const { data: supplier, isLoading, isError, error } = useSupplier(id);
  const update = useUpdateSupplier(id);
  const remove = useDeleteSupplier();
  const unpin = useUnpinAsin(id);

  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [primaryContactName, setPrimaryContactName] = useState('');
  const [status, setStatus] = useState<SupplierPipelineStatus>('lead');
  const [notes, setNotes] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!supplier) return;
    setName(supplier.name);
    setWebsite(supplier.website ?? '');
    setPrimaryEmail(supplier.primaryEmail ?? '');
    setPrimaryContactName(supplier.primaryContactName ?? '');
    setStatus(supplier.status);
    setNotes(supplier.notes ?? '');
  }, [supplier]);

  const dirty =
    !!supplier &&
    (name.trim() !== supplier.name ||
      (website.trim() || null) !== (supplier.website ?? null) ||
      (primaryEmail.trim() || null) !== (supplier.primaryEmail ?? null) ||
      (primaryContactName.trim() || null) !== (supplier.primaryContactName ?? null) ||
      status !== supplier.status ||
      (notes.trim() || null) !== (supplier.notes ?? null));

  async function handleSave() {
    if (!supplier || !dirty) return;
    setFeedback(null);
    try {
      await update.mutateAsync({
        name: name.trim(),
        website: website.trim() || null,
        primaryEmail: primaryEmail.trim() || null,
        primaryContactName: primaryContactName.trim() || null,
        status,
        notes: notes.trim() || null,
      });
      setFeedback('Saved');
    } catch (err) {
      const message =
        err instanceof ApiCallError ? err.message : ((err as Error).message ?? 'Save failed');
      setFeedback(message);
    }
  }

  async function handleDelete() {
    if (!supplier) return;
    if (!confirm(`Delete the supplier "${supplier.name}"? This cannot be undone.`)) return;
    try {
      await remove.mutateAsync(supplier.id);
      navigate({ to: '/suppliers' });
    } catch (err) {
      setFeedback((err as Error).message ?? 'Delete failed');
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-canvas text-fg-primary p-8">
        <p className="text-sm text-fg-secondary">Loading…</p>
      </div>
    );
  }

  if (isError || !supplier) {
    const message =
      error instanceof ApiCallError && error.status === 404
        ? 'Supplier not found.'
        : ((error as Error)?.message ?? 'Unknown error');
    return (
      <div className="min-h-screen bg-canvas text-fg-primary p-8">
        <Card className="border-negative/30 bg-negative/[0.06] max-w-2xl">
          <p className="text-sm text-negative">{message}</p>
          <p className="mt-3">
            <Link to="/suppliers" className="text-accent-fg hover:underline text-sm">
              ← Back to suppliers
            </Link>
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/suppliers"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-xl font-medium leading-tight truncate">{supplier.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant={statusBadgeVariant(supplier.status)}>
                  {STATUS_LABELS[supplier.status]}
                </Badge>
                {supplier.website && (
                  <a
                    href={
                      supplier.website.startsWith('http')
                        ? supplier.website
                        : `https://${supplier.website}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-fg-tertiary hover:text-accent-fg inline-flex items-center gap-1"
                  >
                    {supplier.website} <ExternalLink className="size-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Trash2 className="size-4" />}
              onClick={handleDelete}
              loading={remove.isPending}
            >
              Delete
            </Button>
            <Button onClick={handleSave} loading={update.isPending} disabled={!dirty}>
              {dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-8 grid gap-6 lg:grid-cols-[2fr_3fr]">
        {/* Workflow card */}
        <Card>
          <CardHeader>
            <CardTitle>Workflow</CardTitle>
            <CardDescription>Pipeline status and operator notes.</CardDescription>
          </CardHeader>

          <div className="space-y-4">
            <FormField label="Pipeline status" htmlFor="status">
              <Select value={status} onValueChange={(v) => setStatus(v as SupplierPipelineStatus)}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField label="Notes" htmlFor="notes">
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={5}
                placeholder="What's going on with this supplier?"
                className="w-full rounded-sm border border-border-default bg-surface-1 px-3 py-2 text-sm font-sans text-fg-primary placeholder:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary/40 focus:border-accent-primary/40"
              />
            </FormField>

            <div className="border-t border-border-subtle pt-4">
              <DraftOutreachSection
                supplierStatus={supplier.status}
                supplierId={supplier.id}
                onMarkedSent={(label) => setFeedback(label)}
              />
            </div>
          </div>
        </Card>

        {/* Contact + ASINs */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact</CardTitle>
              <CardDescription>Who and where you reach this supplier.</CardDescription>
            </CardHeader>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Display name" htmlFor="name" required>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
              </FormField>
              <FormField label="Website" htmlFor="website">
                <Input
                  id="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="anker.com"
                />
              </FormField>
              <FormField label="Primary contact" htmlFor="contact">
                <Input
                  id="contact"
                  value={primaryContactName}
                  onChange={(e) => setPrimaryContactName(e.target.value)}
                  placeholder="Sales Team"
                />
              </FormField>
              <FormField label="Primary email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                  placeholder="wholesale@anker.com"
                />
              </FormField>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pinned products ({supplier.asins.length})</CardTitle>
              <CardDescription>
                ASINs you're sourcing from this supplier. Pin from the product detail page.
              </CardDescription>
            </CardHeader>

            {supplier.asins.length === 0 ? (
              <p className="text-sm text-fg-secondary">
                No pinned ASINs yet. Open a product and use{' '}
                <span className="font-medium text-fg-primary">Pin to supplier</span> to attach it
                here.
              </p>
            ) : (
              <ul className="divide-y divide-border-subtle -mx-4">
                {supplier.asins.map((a) => (
                  <li key={a.asin} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to="/products/$asin"
                        params={{ asin: a.asin }}
                        className="font-mono text-sm text-accent-fg hover:underline"
                      >
                        {a.asin}
                      </Link>
                      {a.notes && <p className="text-xs text-fg-tertiary truncate">{a.notes}</p>}
                    </div>
                    <button
                      type="button"
                      aria-label={`Unpin ${a.asin}`}
                      onClick={async () => {
                        if (!confirm(`Unpin ${a.asin} from ${supplier.name}?`)) return;
                        await unpin.mutateAsync(a.asin);
                      }}
                      className="text-fg-tertiary hover:text-negative transition-colors duration-fast"
                    >
                      <Pin className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <PricingSheetsCard supplierId={supplier.id} />

          <MessagesCard supplierId={supplier.id} />

          {feedback && (
            <p
              className={`text-sm ${
                feedback === 'Saved' || feedback === 'Marked as sent'
                  ? 'text-positive'
                  : 'text-negative'
              }`}
            >
              {feedback}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

function messageBadge(msg: {
  direction: 'outbound' | 'inbound';
  status: string;
  classification: 'human_reply' | 'auto_ack' | 'bounce' | null;
}): { label: string; variant: 'neutral' | 'positive' | 'warning' | 'negative' } {
  if (msg.direction === 'outbound') {
    return msg.status === 'failed'
      ? { label: 'failed', variant: 'negative' }
      : { label: msg.status, variant: 'neutral' };
  }
  switch (msg.classification) {
    case 'auto_ack':
      return { label: 'auto-reply', variant: 'warning' };
    case 'bounce':
      return { label: 'bounced', variant: 'negative' };
    default:
      return { label: 'reply', variant: 'positive' };
  }
}

function MessagesCard({ supplierId }: { supplierId: string }) {
  const { data: messages, isLoading } = useSupplierMessages(supplierId);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Messages</CardTitle>
        <CardDescription>
          The outreach thread. Auto-replies and bounces don't count as a supplier response.
        </CardDescription>
      </CardHeader>

      {isLoading ? (
        <p className="text-sm text-fg-tertiary">Loading…</p>
      ) : !messages || messages.length === 0 ? (
        <p className="text-sm text-fg-secondary">No messages yet.</p>
      ) : (
        <ul className="divide-y divide-border-subtle -mx-4">
          {messages.map((msg) => {
            const badge = messageBadge(msg);
            const open = openId === msg.id;
            return (
              <li key={msg.id} className="px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : msg.id)}
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-fg-primary truncate">{msg.subject}</p>
                    <p className="text-xs text-fg-tertiary">
                      {msg.direction === 'outbound' ? 'To supplier' : 'From supplier'} ·{' '}
                      {new Date(msg.sentAt ?? msg.queuedAt).toLocaleString()}
                    </p>
                  </div>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </button>
                {open && (
                  <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-fg-secondary bg-surface-elev rounded-md p-3 max-h-72 overflow-y-auto">
                    {msg.body}
                  </pre>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function PricingSheetsCard({ supplierId }: { supplierId: string }) {
  const { data: sheets, isLoading } = usePricingSheets({ supplierId, limit: 5 });
  const remove = useDeletePricingSheet();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing sheets</CardTitle>
        <CardDescription>
          Upload Excel or CSV pricing sheets to auto-match + ROI-score.
        </CardDescription>
      </CardHeader>

      <div className="space-y-3">
        <PricingSheetUploadModal supplierId={supplierId} />

        {isLoading ? (
          <p className="text-sm text-fg-tertiary">Loading…</p>
        ) : !sheets || sheets.length === 0 ? (
          <p className="text-sm text-fg-secondary">No pricing sheets uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-border-subtle -mx-4">
            {sheets.map((sheet) => (
              <li key={sheet.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-mono text-fg-primary truncate">{sheet.filename}</p>
                  <p className="text-xs text-fg-tertiary">
                    {new Date(sheet.createdAt).toLocaleString()} ·{' '}
                    {Math.ceil(sheet.sizeBytes / 1024)} KB
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={pricingSheetBadgeVariant(sheet.status)}>{sheet.status}</Badge>
                  <button
                    type="button"
                    aria-label={`Delete ${sheet.filename}`}
                    onClick={async () => {
                      if (!confirm(`Delete pricing sheet "${sheet.filename}"?`)) return;
                      await remove.mutateAsync(sheet.id);
                    }}
                    className="text-fg-tertiary hover:text-negative transition-colors duration-fast"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function pricingSheetBadgeVariant(status: string): 'neutral' | 'accent' | 'positive' | 'negative' {
  if (status === 'parsed') return 'accent';
  if (status === 'reviewed') return 'positive';
  if (status === 'parse_failed') return 'negative';
  return 'neutral';
}

/**
 * Outreach button + modal. Pure presentation: takes the current supplier
 * status, decides whether to render the button (lead/contacted/replied)
 * or a disabled-with-explainer block (negotiating/active/passed), and
 * owns the modal lifecycle.
 */
function DraftOutreachSection({
  supplierStatus,
  supplierId,
  onMarkedSent,
}: {
  supplierStatus: SupplierPipelineStatus;
  supplierId: string;
  onMarkedSent: (label: string) => void;
}) {
  const [open, setOpen] = useState(false);

  if (STAGES_WITHOUT_TEMPLATES.includes(supplierStatus)) {
    return (
      <div>
        <Button
          variant="secondary"
          disabled
          leadingIcon={<Mail className="size-4" />}
          title="No outreach template matches this stage."
        >
          Draft outreach email
        </Button>
        <p className="text-xs text-fg-tertiary mt-2">
          Outreach templates ship for stages lead → replied. Move the supplier to a matching stage
          to draft.
        </p>
      </div>
    );
  }

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <Button
          variant="secondary"
          leadingIcon={<Mail className="size-4" />}
          onClick={() => setOpen(true)}
        >
          Draft outreach email
        </Button>
        <DialogContent className="max-w-xl">
          <DraftOutreachForm
            supplierStatus={supplierStatus}
            supplierId={supplierId}
            onClose={() => setOpen(false)}
            onMarkedSent={(label) => {
              onMarkedSent(label);
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DraftOutreachForm({
  supplierStatus,
  supplierId,
  onClose,
  onMarkedSent,
}: {
  supplierStatus: SupplierPipelineStatus;
  supplierId: string;
  onClose: () => void;
  onMarkedSent: (label: string) => void;
}) {
  const draft = useDraftOutreach();
  const update = useUpdateSupplier(supplierId);

  // Default selection = the kind that matches the current status.
  const defaultKind = useMemo<OutreachTemplateKind>(() => {
    const match = (
      Object.entries(TEMPLATE_REQUIRED_STATUS) as Array<
        [OutreachTemplateKind, SupplierPipelineStatus]
      >
    ).find(([, required]) => required === supplierStatus);
    return match?.[0] ?? 'cold_intro';
  }, [supplierStatus]);

  const [kind, setKind] = useState<OutreachTemplateKind>(defaultKind);
  const [result, setResult] = useState<DraftOutreachResponse | null>(null);
  const [composer, dispatchComposer] = useReducer(
    outreachComposerReducer,
    initialOutreachComposerState
  );
  const [copied, setCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When the user picks a different template kind after generating,
  // discard the stale draft so the UI doesn't show a draft for one
  // kind alongside the dropdown showing another. The Generate
  // button reappears so they can produce a fresh draft.
  useEffect(() => {
    setResult(null);
    dispatchComposer({ type: 'seed', subject: '', body: '' });
    setCopied(false);
  }, [kind]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const allKinds: OutreachTemplateKind[] = ['cold_intro', 'follow_up', 'pricing_request'];

  function handleKindChange(nextKind: OutreachTemplateKind) {
    draft.reset();
    setKind(nextKind);
  }

  async function handleGenerate() {
    draft.reset();
    setResult(null);
    setCopied(false);
    try {
      const response = await draft.mutateAsync({ supplierId, templateKind: kind });
      setResult(response);
      dispatchComposer({ type: 'seed', subject: response.subject, body: response.body });
    } catch {
      // surfaced via draft.error
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(`Subject: ${composer.subject}\n\n${composer.body}`);
      setCopied(true);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        copyResetTimeoutRef.current = null;
      }, 2000);
    } catch {
      // best-effort; clipboard may be denied in non-secure contexts
    }
  }

  async function handleMarkSent() {
    const nextStatus = MARK_SENT_TRANSITIONS[kind];
    // Belt-and-braces: the button is disabled when there's no transition,
    // but guard here too so "Marked as sent" never fires without an
    // actual status change.
    if (!nextStatus) return;
    try {
      await update.mutateAsync({ status: nextStatus });
      onMarkedSent('Marked as sent');
    } catch {
      // surfaced via update.error below
    }
  }

  const nextStatus = MARK_SENT_TRANSITIONS[kind];
  const markSentTitle = nextStatus
    ? `Advance pipeline to "${nextStatus}".`
    : "Follow-ups don't advance the pipeline — the next stage (replied) is the supplier's action.";

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>Draft outreach email</DialogTitle>
        <DialogDescription>
          One-shot LLM draft via your OpenRouter key. Edit before copying — Sourcito never sends on
          your behalf.
        </DialogDescription>
      </DialogHeader>

      <FormField label="Template" htmlFor="template-kind">
        <Select value={kind} onValueChange={(v) => handleKindChange(v as OutreachTemplateKind)}>
          <SelectTrigger id="template-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allKinds.map((k) => {
              const required = TEMPLATE_REQUIRED_STATUS[k];
              const disabled = required !== supplierStatus;
              return (
                <SelectItem key={k} value={k} className={disabled ? 'opacity-50' : ''}>
                  {TEMPLATE_LABELS[k]}
                  {disabled ? ` (needs status: ${required})` : ''}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </FormField>

      {!result && (
        <div className="flex justify-end">
          <Button onClick={handleGenerate} loading={draft.isPending}>
            Generate draft
          </Button>
        </div>
      )}

      {draft.isError && (
        <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
          <p className="text-sm text-negative">{(draft.error as Error).message}</p>
        </div>
      )}

      {result && (
        <>
          <OutreachComposer
            supplierId={supplierId}
            state={composer}
            dispatch={dispatchComposer}
            disabled={update.isPending}
          />

          <p className="text-xs text-fg-tertiary">
            {result.model} · prompt v{result.promptVersion} · {result.usage.inputTokens} in /{' '}
            {result.usage.outputTokens} out
          </p>

          {update.isError && (
            <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
              <p className="text-sm text-negative">{(update.error as Error).message}</p>
            </div>
          )}
        </>
      )}

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
        {result && (
          <>
            <Button type="button" variant="secondary" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy to clipboard'}
            </Button>
            <Button
              type="button"
              onClick={handleMarkSent}
              loading={update.isPending}
              title={markSentTitle}
              disabled={!nextStatus}
            >
              Mark as sent
            </Button>
          </>
        )}
      </DialogFooter>
    </div>
  );
}
